import React, { useState, useEffect } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { updateData, fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import '../Customer/Customer.css';

const BankEdit = () => {
    const { id: recordId } = useParams();
    const history = useHistory();
    const { isAuthenticated, companyId, branchId, userId, logout, token } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, companyId, branchId, token, logout]);

    const [formData, setFormData] = useState({
        BankName: '',
        IFSC: '',
        AccountNo: '',
        Branch: '',
        IsPayable: true,
        OpeningBalance: 0,
        PaymentQRcodeUPIID: '',
        UPIName: '',
        IsPrimary: false,
        CompanyId: companyId,
    });

    useEffect(() => {
        fetchData('BankApi', `/bank/${recordId}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setFormData({
                        ...response.response_data,
                        UpdatedBy: userId,
                    });
                }
            })
            .catch((error) => {
                console.error('Error fetching Bank Details:', error);
            });
    }, [recordId]);

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : type === 'radio' ? value === 'true' : value,
        }));
    };

    const submitData = (event) => {
        event.preventDefault();
        setIsLoading(true);
        setErrorMessage('');

        if (!formData.BankName) {
            setErrorMessage('Bank Name is required!');
            setIsLoading(false);
            return;
        }

        if (!formData.AccountNo) {
            setErrorMessage('Account Number is required!');
            setIsLoading(false);
            return;
        }

        if (!formData.IFSC) {
            setErrorMessage('IFSC Code is required!');
            setIsLoading(false);
            return;
        }

        const CreateData = {
            ...formData,
            UpdatedBy: userId,
            CompanyId: companyId,
        };

        updateData('BankApi', `${recordId}`, CreateData, token)
            .then(() => {
                setSuccessMessage('Bank updated successfully!');
                history.push('/BankIndex');
            })
            .catch((error) => {
                console.error('Error updating record:', error);
                setErrorMessage('Failed to update bank.');
                setIsLoading(false);
            });
    };

    return (
        <div className="form-container">
            <div className="form-card">
                {/* Header */}
                <div className="form-header">
                    <h1 className="form-title">Edit Bank</h1>
                    <div className="form-actions">
                        <Link to="/BankIndex" className="btn-back">
                            <ArrowBackIcon fontSize="small" />
                            Back to List
                        </Link>
                        <button className="btn-save" onClick={submitData} disabled={isLoading}>
                            {isLoading ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : (
                                <>
                                    <SaveIcon fontSize="small" />
                                    Update
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Messages */}
                {errorMessage && <div className="message error">{errorMessage}</div>}
                {successMessage && <div className="message success">{successMessage}</div>}

                {/* Form Body */}
                <div className="form-body">
                    {/* Row 1 - Bank Name */}
                    <div className="form-row">
                        <div className="form-group full-width">
                            <label className="form-label">Bank Name *</label>
                            <input
                                className="form-input"
                                name="BankName"
                                value={formData.BankName || ''}
                                onChange={handleChange}
                                type="text"
                                placeholder="Enter bank name"
                                maxLength={50}
                            />
                        </div>
                    </div>

                    {/* Row 2 - IFSC, Account, Branch, Balance, Payable */}
                    <div className="form-row">
                        <div className="form-group">
                            <label className="form-label">IFSC Code *</label>
                            <input
                                className="form-input"
                                name="IFSC"
                                value={formData.IFSC || ''}
                                onChange={handleChange}
                                type="text"
                                placeholder="Enter IFSC code"
                                maxLength={30}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Account Number *</label>
                            <input
                                className="form-input"
                                name="AccountNo"
                                value={formData.AccountNo || ''}
                                onChange={handleChange}
                                type="text"
                                placeholder="Enter account number"
                                maxLength={40}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Branch Name</label>
                            <input
                                className="form-input"
                                name="Branch"
                                value={formData.Branch || ''}
                                onChange={handleChange}
                                type="text"
                                placeholder="Enter branch name"
                                maxLength={50}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Opening Balance</label>
                            <input
                                className="form-input"
                                name="OpeningBalance"
                                value={formData.OpeningBalance || ''}
                                onChange={handleChange}
                                type="number"
                                placeholder="0.00"
                                style={{ textAlign: 'right' }}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">&nbsp;</label>
                            <div style={{ display: 'flex', alignItems: 'center', height: '42px', gap: '8px' }}>
                                <input
                                    type="checkbox"
                                    name="IsPayable"
                                    checked={formData.IsPayable || false}
                                    onChange={handleChange}
                                    style={{ width: '18px', height: '18px', accentColor: '#1e3a5f', cursor: 'pointer' }}
                                />
                                <span style={{ fontSize: '14px', color: '#475569' }}>Is Payable?</span>
                            </div>
                        </div>
                    </div>

                    {/* Row 3 - UPI Details */}
                    <div className="form-row">
                        <div className="form-group">
                            <label className="form-label">Payment QR UPI ID</label>
                            <input
                                className="form-input"
                                name="PaymentQRcodeUPIID"
                                value={formData.PaymentQRcodeUPIID || ''}
                                onChange={handleChange}
                                type="text"
                                placeholder="Enter UPI ID"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Payment QR UPI Name</label>
                            <input
                                className="form-input"
                                name="UPIName"
                                value={formData.UPIName || ''}
                                onChange={handleChange}
                                type="text"
                                placeholder="Enter UPI name"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">&nbsp;</label>
                            <div style={{ display: 'flex', alignItems: 'center', height: '42px', gap: '8px' }}>
                                <input
                                    type="checkbox"
                                    name="IsPrimary"
                                    checked={formData.IsPrimary || false}
                                    onChange={handleChange}
                                    style={{ width: '18px', height: '18px', accentColor: '#059669', cursor: 'pointer' }}
                                />
                                <span style={{ fontSize: '14px', color: '#475569' }}>Is Primary?</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BankEdit;
